import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';
import { MediaDropZone } from 'v5/patterns/forms/fields/image-upload/MediaDropZone';
import { useClipboardFiles } from 'v5/patterns/forms/fields/image-upload/useClipboardFiles';
import { GalleryEditor } from 'v5/patterns/widgets/gallery-editor/GalleryEditor';
import type { PinLinkWrite } from './pinboard.logic';
import { usePinLink } from './usePinLink';

export interface AddPinsDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  /** Files to upload and pin; the dialog closes, the board shows their progress. */
  onFiles(files: File[]): void;
  onAddLink(write: PinLinkWrite): void;
  /** An address to start the link field with, e.g. one pasted over the board. */
  initialLink?: string;
}

/**
 * Where pins are added from: files dropped on the zone, chosen or pasted (⌘V while focus is
 * inside), or a link: a picture, a YouTube or Vimeo video, any web page.
 * In Add pins, and in place of an empty board.
 */
export function AddPinsZone({
  onFiles,
  onAddLink,
  initialLink
}: {
  onFiles(files: File[]): void;
  onAddLink(write: PinLinkWrite): void;
  /** An address to start the link field with, e.g. one pasted over the board. */
  initialLink?: string;
}) {
  const { t } = useTranslation('resources', { keyPrefix: 'Pinboard.add' });
  const take = (files: File[]) => {
    if (files.length > 0) onFiles(files);
  };
  const clipboard = useClipboardFiles({ accepts: () => true, onFiles: take });
  const link = usePinLink(onAddLink, initialLink);
  return (
    <div className="flex w-full flex-col gap-4" onPaste={clipboard.onPaste}>
      <MediaDropZone
        multiple
        description={t('formats')}
        preview={link.preview}
        onFiles={take}
      />
      <div className="max-w-150">
        <GalleryEditor.LinkField label={t('orAddLink')} {...link.field} />
      </div>
    </div>
  );
}

/** `AddPinsZone` in a dialog, which closes once something is added. */
export function AddPinsDialog({
  open,
  onOpenChange,
  onFiles,
  onAddLink,
  initialLink
}: AddPinsDialogProps) {
  const { t } = useTranslation('resources', { keyPrefix: 'Pinboard.add' });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-150">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="pb-6">
          <AddPinsZone
            initialLink={initialLink}
            onFiles={files => {
              onFiles(files);
              onOpenChange(false);
            }}
            onAddLink={write => {
              onAddLink(write);
              onOpenChange(false);
            }}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
